import {
  ELEMENT_P,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  ELEMENT_TABLE,
  ELEMENT_TABLE_HEAD,
  getEmptyParagraph,
  getRow,
  getTableBody,
  getTableHead,
  ELEMENT_TABLE_BODY,
} from 'bxl-editor'
import { Node } from 'slate'

interface TableRow {
  [key: string]: string | undefined | null
}

interface TableData {
  title?: string
  desc?: string
  columns?: string[]
  content?: TableRow[]
}

export interface SizeChart {
  id: string
  brand?: string
  brandName?: string
  categoryIds?: string[]
  tables?: TableData[]
}

export interface EditorElement {
  type: string
  children: EditorElement[]
  [key: string]: any
}

const HEADING_TYPES: string[] = [ELEMENT_H1, ELEMENT_H2, ELEMENT_H3, ELEMENT_H4, ELEMENT_H5, ELEMENT_H6]

class SizeChartDataAdapter {
  generateTableRows(columns: string[], rows: TableRow[]): any[] {
    const trs = rows.map((r: TableRow, rowIndex: number) => {
      const tds: string[] = columns.map(c => r?.[c] || '')
      return getRow(rowIndex + 1, tds)
    })
    return trs
  }

  deseializeTable(tableData: TableData): EditorElement[] {
    const tableContent: any[] = []
    if (tableData.title) {
      tableContent.push({ type: ELEMENT_H1, children: [{ text: tableData.title }] })
    }
    if (tableData.desc) {
      tableContent.push({ type: ELEMENT_P, children: [{ text: tableData.desc }] })
    }
    const tableColumnLen = tableData.columns?.length || 0
    if (tableColumnLen && Array.isArray(tableData.content)) {
      const trs = this.generateTableRows(tableData.columns!!, tableData.content!!)
      tableContent.push({
        type: ELEMENT_TABLE,
        children: [
          getTableHead(tableData.columns!!),
          getTableBody(trs),
        ].filter(Boolean),
      })
    }
    return tableContent
  }

  deseialize(sizeChart?: SizeChart) {
    if (!sizeChart) return [getEmptyParagraph()]
    if (Array.isArray(sizeChart.tables)) {
      return sizeChart.tables.map(t => this.deseializeTable(t)).reduce((curr, value) => curr.concat(value), [])
    }
    return [getEmptyParagraph()]
  }

  getText(ele?: EditorElement): string {
    if (!ele) return ''
    return Node.string(ele)?.trim() || ''
  }

  // 目前必然存在 columns 
  serializeColumns(ele?: EditorElement): string[] {
    const columns: string[] = []
    if (!ele || ele.type !== ELEMENT_TABLE) return columns
    if (ele.children?.[0]?.type === ELEMENT_TABLE_HEAD) {
      // tr
      const headRow = ele.children?.[0]?.children?.[0]
      headRow?.children?.forEach(th => {
        const text = this.getText(th)
        columns.push(text)
      })
    }
    return columns
  }

  // 基于 thead 存在， tbody 在 index = 1
  serializeTableRow(columns: string[], ele?: EditorElement): TableRow[] {
    if (!ele || ele.type !== ELEMENT_TABLE) return []
    const rows: TableRow[] = []
    if (ele.children?.[1]?.type === ELEMENT_TABLE_BODY) {
      // tr
      ele.children?.[1]?.children?.forEach(tr => {
        const tableRow: TableRow = {}
        columns.forEach((key: string, index: number) => {
          // td
          const td = tr.children?.[index]
          tableRow[key] = this.getText(td)
        })
        rows.push(tableRow)
      })
    }
    return rows
  }

  validate(editorContent: EditorElement[] | null | undefined): string | null {
    if (!Array.isArray(editorContent)) return null
    let errors: string | null = null
    let tableCount = 0
    editorContent.forEach((ele: EditorElement) => {
      switch (ele.type) {
        case ELEMENT_TABLE:
          tableCount += 1
          if (ele.children?.[0]?.type !== ELEMENT_TABLE_HEAD) {
            errors = '尺码表必须要设置表头!'
          }
          break
      }
    })
    if (!tableCount) {
      errors = '尺码表格缺失!'
    }
    return errors
  }

  serialize(editorContent: EditorElement[] | null | undefined): TableData[] {
    if (!Array.isArray(editorContent)) return []
    const result: TableData[] = []
    let tempData: TableData = {}
    editorContent.forEach((ele: EditorElement) => {
      switch (ele.type) {
        case ELEMENT_P:
          if (!tempData.desc) {
            // only set the first desc
            tempData.desc = this.getText(ele)
          }
          break
        case ELEMENT_H1:
        case ELEMENT_H2:
        case ELEMENT_H3:
        case ELEMENT_H4:
        case ELEMENT_H5:
        case ELEMENT_H6:
          if (!tempData.title) {
            // only set the first title
            tempData.title = this.getText(ele)
          }
          break
        case ELEMENT_TABLE:
          const columns = this.serializeColumns(ele)
          if (Array.isArray(columns) && columns.length) {
            tempData.columns = columns
          }
          const tableRows: TableRow[] = this.serializeTableRow(columns, ele)
          if (Array.isArray(tableRows) && tableRows.length) {
            tempData.content = tableRows
          }
          result.push(tempData)
          tempData = {}
          break
      }
    })
    return result
  }
}

export default SizeChartDataAdapter
